import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";
import { Button } from "@recobra/ui/button";
import { ExpandableText } from "@recobra/ui/custom/expandable-text";
import { DialogFooter } from "@recobra/ui/dialog";

import { UserLabel } from "../../lib";
import { RequirementChecklist } from "../requirement-checklist";
import { useTaskName } from "../task-data";
import { TaskContextStatus } from "../task-details";
import { TaskDetailsSummary } from "../task-details";
import type { TaskMetadataEditor } from "../task-details";
import type { TaskRequirementContext } from "../task-details";
import { findTaskExecutionEntry, formatTaskTimestamp } from "../task-display";
import { TaskModalSection, TaskSummaryList, TaskSummaryRow } from "../task-display";
import { LiabilityReviewedValues } from "./LiabilityReviewedValues";

/**
 * What actually happened, read-only: the same facts the executor saw, plus
 * the recorded outcome. Assignee only shows for people who could otherwise edit.
 */
export const TaskCompletionRecord: FC<{
  task: Task;
  context: TaskRequirementContext;
  editor: TaskMetadataEditor;
  onClose: () => void;
}> = ({ task, context, editor, onClose }) => {
  const executedEntry = findTaskExecutionEntry(task);
  const taskName = useTaskName();
  const resolution = context.taskType?.resolutions.find(({ id }) => id === task.solution);
  const nextTaskType = task.completionEffects?.nextTaskType;
  const hasOneOutcome = context.taskType?.resolutions.length === 1;

  return (
    <>
      <div className="-mx-4 max-h-(--task-content-max-height) overflow-y-auto px-4">
        <div className="flex flex-col divide-y">
          <TaskDetailsSummary task={task} editor={editor} />
          <TaskContextStatus context={context} />

          {resolution && !context.error ? (
            <RequirementChecklist
              task={task}
              flow={context.taskType}
              resolutionId={resolution.id}
              editor={editor}
              disabled
              handlers={{ onSatisfy: () => undefined, onWaive: () => undefined }}
            />
          ) : null}

          <LiabilityReviewedValues
            taskType={context.taskType}
            values={task.liabilityReviewSnapshot}
          />

          <TaskModalSection>
            <TaskSummaryList>
              {hasOneOutcome ? null : (
                <TaskSummaryRow label={m.task_outcome_select_label()}>
                  {recordedSolutionLabel(task.solution, resolution?.label)}
                </TaskSummaryRow>
              )}

              {nextTaskType ? (
                <TaskSummaryRow label={m.task_next_task_label()}>
                  {taskName(nextTaskType)}
                </TaskSummaryRow>
              ) : null}

              {task.system === "analyze_case_liability" && task.declineReason ? (
                <TaskSummaryRow label={m.task_decline_reason_label()}>
                  {m.task_decline_reason_name({ reason: task.declineReason })}
                </TaskSummaryRow>
              ) : null}

              {executedEntry?.notes ? (
                <TaskSummaryRow label={m.task_outcome_notes_label()}>
                  <ExpandableText text={executedEntry.notes} scrollable />
                </TaskSummaryRow>
              ) : null}

              {executedEntry ? (
                <TaskSummaryRow label={m.task_completed_by_label()}>
                  <div className="flex items-center gap-2">
                    <UserLabel userId={executedEntry.updatedBy} />
                    <span className="text-xs text-muted-foreground">
                      {formatTaskTimestamp(executedEntry.updatedAt)}
                    </span>
                  </div>
                </TaskSummaryRow>
              ) : null}
            </TaskSummaryList>
          </TaskModalSection>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {m.dialog_close()}
        </Button>
      </DialogFooter>
    </>
  );
};

/** The recorded resolution; one the flow no longer has reads as legacy. */
const recordedSolutionLabel = (
  solution: string | null | undefined,
  label: string | undefined,
): string => {
  if (!solution) return m.common_em_dash();
  return label ?? m.task_solution_legacy({ solution });
};
